import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PRIMARY } from '../styles/shared';
import { trackEvent } from '../utils/analytics';

const STORAGE_KEY = 'bragsheet_onboarding_complete';

const steps = [
  {
    title: 'Welcome to BragSheet!',
    description:
      'BragSheet helps you track your achievements, activities, and reflections throughout high school so you are ready when college application season arrives.',
    icon: '🎓',
  },
  {
    title: 'Start Your Journal',
    description:
      'Create journal entries to document accomplishments, community service hours, leadership moments, and personal growth. Organize them by category to easily find what you need later.',
    icon: '📝',
    cta: { label: 'Write Your First Entry', path: '/journal/new' },
  },
  {
    title: 'Build Your Profile',
    description:
      'Add your GPA, test scores, extracurricular activities, and awards. A complete profile helps generate better essay ideas and keeps everything in one place.',
    icon: '👤',
    cta: { label: 'Complete Your Profile', path: '/profile' },
  },
  {
    title: 'AI-Powered Insights',
    description:
      'BragSheet can auto-tag your entries and brainstorm college essay themes based on your unique experiences. The more you journal, the better the suggestions become.',
    icon: '✨',
  },
];

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9000,
  padding: '1rem',
};

const modalStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  maxWidth: '480px',
  width: '100%',
  padding: '2.5rem 2rem 2rem',
  position: 'relative',
  animation: 'fadeInScale 0.25s ease-out',
};

const iconCircleStyle = {
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  backgroundColor: PRIMARY + '15',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.75rem',
  margin: '0 auto 1.25rem',
};

const titleStyle = {
  fontSize: '1.35rem',
  fontWeight: '700',
  color: '#1e293b',
  textAlign: 'center',
  marginBottom: '0.75rem',
};

const descStyle = {
  fontSize: '0.925rem',
  color: '#475569',
  textAlign: 'center',
  lineHeight: '1.6',
  marginBottom: '1.5rem',
};

const footerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '1rem',
};

const dotsContainerStyle = {
  display: 'flex',
  gap: '0.4rem',
  justifyContent: 'center',
};

const btnBase = {
  padding: '0.6rem 1.2rem',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: '600',
  transition: 'opacity 0.15s',
  minHeight: '40px',
};

const primaryBtnStyle = {
  ...btnBase,
  backgroundColor: PRIMARY,
  color: '#ffffff',
};

const skipBtnStyle = {
  ...btnBase,
  backgroundColor: 'transparent',
  color: '#64748b',
};

const ctaBtnStyle = {
  display: 'inline-block',
  padding: '0.55rem 1.1rem',
  borderRadius: '8px',
  backgroundColor: PRIMARY + '12',
  color: PRIMARY,
  fontSize: '0.85rem',
  fontWeight: '600',
  textDecoration: 'none',
  border: `1px solid ${PRIMARY}40`,
  cursor: 'pointer',
  textAlign: 'center',
};

export function useOnboardingTour() {
  const [showTour, setShowTour] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) !== 'true';
  });

  const openTour = useCallback(() => setShowTour(true), []);
  const closeTour = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShowTour(false);
  }, []);

  return { showTour, openTour, closeTour };
}

export default function OnboardingTour({ onClose, onNavigate }) {
  const [currentStep, setCurrentStep] = useState(0);
  // Track via ref so the analytics fire can see the latest step count even
  // when called from event handlers that closed over a stale `currentStep`.
  // `steps_seen`/`steps_completed` is 1-indexed (the number of steps the user
  // actually viewed), which is more useful in GA4 than the 0-indexed state.
  const stepRef = useRef(0);
  useEffect(() => { stepRef.current = currentStep; }, [currentStep]);

  const complete = useCallback((reason) => {
    localStorage.setItem(STORAGE_KEY, 'true');
    // reason: 'completed' | 'skipped' | 'cta'
    //   - 'completed': user hit "Get Started" on the final step
    //   - 'skipped':   ESC, backdrop click, or "Skip" button
    //   - 'cta':       user clicked a step's CTA (e.g. "Write Your First Entry").
    //                  Counts as completion intent, not a skip.
    const stepsSeen = stepRef.current + 1;
    if (reason === 'completed' || reason === 'cta') {
      trackEvent('onboarding_completed', { steps_completed: stepsSeen });
    } else {
      trackEvent('onboarding_skipped', { steps_seen: stepsSeen });
    }
    onClose();
  }, [onClose]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      complete('completed');
    }
  };

  const handleSkip = () => {
    complete('skipped');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      complete('skipped');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        complete('skipped');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [complete]);

  const step = steps[currentStep];

  return (
    <>
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div
        style={overlayStyle}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-label="Onboarding tour"
      >
        <div style={modalStyle}>
          <div style={iconCircleStyle}>{step.icon}</div>
          <h2 style={titleStyle}>{step.title}</h2>
          <p style={descStyle}>{step.description}</p>

          {step.cta && (
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <button
                style={ctaBtnStyle}
                onClick={() => {
                  complete('cta');
                  if (onNavigate) onNavigate(step.cta.path);
                }}
              >
                {step.cta.label}
              </button>
            </div>
          )}

          <div style={dotsContainerStyle}>
            {steps.map((_, i) => (
              <div
                key={i}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: i === currentStep ? PRIMARY : '#cbd5e1',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </div>

          <div style={footerStyle}>
            <button style={skipBtnStyle} onClick={handleSkip}>
              Skip
            </button>
            <button style={primaryBtnStyle} onClick={handleNext}>
              {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
